import type { LevelDef } from '../sim/types';
import { GENERATED_LEVELS } from './generated';
import { CODA_LEVELS } from './coda';
import { generateLevel, isProceduralId, proceduralIndex, isDailyId, dailyKeyFromId, generateDailyLevel } from './procgen';

/**
 * Levels are authored on a 1080×1920 canvas with x ∈ [180, 900] and
 * y ∈ [360, 1620]. (Earlier authored at 720×1280 — all coords scaled ×1.5.)
 *
 * NODE SPACING RULE: every pair of node centers must be ≥195px apart
 * (visual radius 51, hit radius 84, so closer than 195 makes the hit
 * zones bleed into each other).
 */

// ─────────────────────────────────────────────────────────────────────────────
// LEVEL 1 — First contact
// ─────────────────────────────────────────────────────────────────────────────
const L1: LevelDef = {
  id: 'l1',
  name: 'First Contact',
  tier: 'tutorial',
  teaching: true,
  intro: 'Tap any node. Watch the cascade.',
  hint: 'There is no wrong answer here.',
  nodes: [
    { id: 's',  x: 300, y: 960, type: 'normal',   label: 'S' },
    { id: 'm',  x: 540, y: 960, type: 'normal',   label: 'M' },
    { id: 'r',  x: 780, y: 960, type: 'required', label: 'R' },
  ],
  wires: [
    { id: 'w1', a: 's', b: 'm' },
    { id: 'w2', a: 'm', b: 'r' },
  ],
  objective: { requiredNodeIds: ['r'] },
};

// ─────────────────────────────────────────────────────────────────────────────
// LEVEL 2 — Topology
// ─────────────────────────────────────────────────────────────────────────────
const L2: LevelDef = {
  id: 'l2',
  name: 'Topology',
  tier: 'beginner',
  intro: 'The cascade only flows one way.',
  hint: 'Look at the arrows. The current cannot run uphill.',
  nodes: [
    { id: 's',  x: 540, y: 420, type: 'normal',   label: 'S' },
    { id: 'r1', x: 300, y: 780, type: 'required', label: 'R1' },
    { id: 'r2', x: 780, y: 780, type: 'required', label: 'R2' },
    { id: 'r3', x: 300, y: 1230, type: 'required', label: 'R3' },
    { id: 'r4', x: 780, y: 1230, type: 'required', label: 'R4' },
  ],
  wires: [
    { id: 'w1', a: 's',  b: 'r1', type: 'directional' },
    { id: 'w2', a: 's',  b: 'r2', type: 'directional' },
    { id: 'w3', a: 'r1', b: 'r3', type: 'directional' },
    { id: 'w4', a: 'r2', b: 'r4', type: 'directional' },
  ],
  objective: { requiredNodeIds: ['r1', 'r2', 'r3', 'r4'] },
};

// ─────────────────────────────────────────────────────────────────────────────
// LEVEL 3 — Three candidates
// ─────────────────────────────────────────────────────────────────────────────
const L3: LevelDef = {
  id: 'l3',
  name: 'Three Candidates',
  tier: 'beginner',
  intro: 'Three breach points. Only one reaches all.',
  nodes: [
    { id: 's1', x: 270, y: 420, type: 'normal',   label: 'S1' },
    { id: 's2', x: 540, y: 420, type: 'normal',   label: 'S2' },
    { id: 's3', x: 810, y: 420, type: 'normal',   label: 'S3' },
    { id: 'r1', x: 240, y: 1140, type: 'required', label: 'R1' },
    { id: 'r2', x: 450, y: 1140, type: 'required', label: 'R2' },
    { id: 'r3', x: 660, y: 1140, type: 'required', label: 'R3' },
    { id: 'r4', x: 870, y: 1140, type: 'required', label: 'R4' },
  ],
  wires: [
    { id: 'w1', a: 's1', b: 'r1', type: 'directional' },
    { id: 'w2', a: 's1', b: 'r2', type: 'directional' },
    { id: 'w3', a: 's2', b: 'r2', type: 'directional' },
    { id: 'w4', a: 's2', b: 'r3', type: 'directional' },
    { id: 'w5', a: 's3', b: 'r1', type: 'directional' },
    { id: 'w6', a: 's3', b: 'r2', type: 'directional' },
    { id: 'w7', a: 's3', b: 'r3', type: 'directional' },
    { id: 'w8', a: 's3', b: 'r4', type: 'directional' },
  ],
  objective: { requiredNodeIds: ['r1', 'r2', 'r3', 'r4'] },
};

// ─────────────────────────────────────────────────────────────────────────────
// LEVEL 4 — Quarantine
// ─────────────────────────────────────────────────────────────────────────────
const L4: LevelDef = {
  id: 'l4',
  name: 'Quarantine',
  tier: 'easy',
  intro: 'Red nodes are off limits. Find a clean breach.',
  nodes: [
    { id: 's1', x: 270, y: 420, type: 'normal',    label: 'S1' },
    { id: 's2', x: 810, y: 420, type: 'normal',    label: 'S2' },
    { id: 'x',  x: 270, y: 750, type: 'normal',    label: 'X' },
    { id: 'r1', x: 540, y: 1080, type: 'required',  label: 'R1' },
    { id: 'r2', x: 810, y: 1080, type: 'required',  label: 'R2' },
    { id: 'p',  x: 270, y: 1380, type: 'protected', label: 'P' },
  ],
  wires: [
    { id: 'w1', a: 's1', b: 'x',  type: 'directional' },
    { id: 'w2', a: 'x',  b: 'r1', type: 'directional' },
    { id: 'w3', a: 'x',  b: 'p',  type: 'directional' },
    { id: 'w4', a: 's2', b: 'r1', type: 'directional' },
    { id: 'w5', a: 'r1', b: 'r2', type: 'directional' },
  ],
  objective: { requiredNodeIds: ['r1', 'r2'] },
};

// ─────────────────────────────────────────────────────────────────────────────
// LEVEL 5 — Convergence
// ─────────────────────────────────────────────────────────────────────────────
const L5: LevelDef = {
  id: 'l5',
  name: 'Convergence',
  tier: 'medium',
  intro: 'Some nodes need more than one surge to fall.',
  hint: 'Find a breach that splits into two parallel paths.',
  nodes: [
    { id: 's',  x: 540, y: 420, type: 'normal',   label: 'S' },
    { id: 'd',  x: 540, y: 1500,type: 'normal',   label: 'D' },
    { id: 'r1', x: 240, y: 840, type: 'required', label: 'R1' },
    { id: 'a',  x: 570, y: 750, type: 'normal',   label: 'A' },
    { id: 'b',  x: 870, y: 750, type: 'normal',   label: 'B' },
    { id: 'r2', x: 705, y: 1140, type: 'required', threshold: 2, label: 'R2' },
  ],
  wires: [
    { id: 'w1', a: 's',  b: 'r1', type: 'directional' },
    { id: 'w2', a: 's',  b: 'a',  type: 'directional' },
    { id: 'w3', a: 's',  b: 'b',  type: 'directional' },
    { id: 'w4', a: 'a',  b: 'r2', type: 'directional' },
    { id: 'w5', a: 'b',  b: 'r2', type: 'directional' },
    { id: 'w6', a: 'd',  b: 'r2', type: 'directional' },
  ],
  objective: { requiredNodeIds: ['r1', 'r2'] },
};

// ─────────────────────────────────────────────────────────────────────────────
// LEVEL 6 — Timing
// ─────────────────────────────────────────────────────────────────────────────
const L6: LevelDef = {
  id: 'l6',
  name: 'Timing',
  tier: 'hard',
  intro: 'Charge fades. Two pulses must land while the first is still hot.',
  hint: 'A delay wire is a buffer, and a trap if you use it wrong.',
  nodes: [
    { id: 's1', x: 270, y: 420, type: 'normal',   label: 'S1' },
    { id: 's2', x: 810, y: 420, type: 'normal',   label: 'S2' },
    { id: 'cap',x: 270, y: 810, type: 'capacitor',label: 'CAP' },
    { id: 'a',  x: 540, y: 810, type: 'normal',   label: 'A' },
    { id: 'b',  x: 810, y: 810, type: 'normal',   label: 'B' },
    { id: 'r1', x: 300, y: 1230, type: 'required', label: 'R1' },
    { id: 'r2', x: 690, y: 1230, type: 'required', threshold: 2, decay: 2, label: 'R2' },
  ],
  wires: [
    { id: 'w1', a: 's1', b: 'r2',  type: 'directional' },
    { id: 'w2', a: 's1', b: 'cap', type: 'directional' },
    { id: 'w3', a: 'cap',b: 'r2',  type: 'delay' },
    { id: 'w4', a: 's2', b: 'r1',  type: 'directional' },
    { id: 'w5', a: 's2', b: 'a',   type: 'directional' },
    { id: 'w6', a: 's2', b: 'b',   type: 'directional' },
    { id: 'w7', a: 'a',  b: 'r2',  type: 'directional' },
    { id: 'w8', a: 'b',  b: 'r2',  type: 'directional' },
    { id: 'w9', a: 's1', b: 'r1',  type: 'directional' },
  ],
  objective: { requiredNodeIds: ['r1', 'r2'] },
};

// ─────────────────────────────────────────────────────────────────────────────
// LEVEL 7 — Signal Collapse
// ─────────────────────────────────────────────────────────────────────────────
const L7: LevelDef = {
  id: 'l7',
  name: 'Signal Collapse',
  tier: 'expert',
  intro: 'Three starters. One opens a relay, drops the shield, and floods the core.',
  hint: 'A direct hit on a shielded core does nothing. Drop the shield first.',
  nodes: [
    { id: 'a',   x: 240, y: 420, type: 'normal',    label: 'A' },
    { id: 'c',   x: 540, y: 420, type: 'normal',    label: 'C' },
    { id: 'b',   x: 840, y: 420, type: 'normal',    label: 'B' },
    { id: 'r',   x: 300, y: 720, type: 'relay',     unlocks: ['l1'], label: 'R' },
    { id: 'cap', x: 780, y: 720, type: 'capacitor', label: 'CAP' },
    { id: 'p',   x: 210, y: 1080, type: 'protected', label: 'P' },
    { id: 'sp',  x: 510, y: 1080, type: 'splitter',  splitFactor: 3, label: 'SP' },
    { id: 's',   x: 810, y: 1080, type: 'shield',    shields: ['core'], label: 'S' },
    { id: 'core',x: 540, y: 1470,type: 'core',      threshold: 3, decay: 3, label: 'CORE' },
  ],
  wires: [
    { id: 'wA_R',    a: 'a',   b: 'r',    type: 'directional' },
    { id: 'wA_P',    a: 'a',   b: 'p',    type: 'directional' },
    { id: 'wB_CORE', a: 'b',   b: 'core', type: 'directional' },
    { id: 'wC_R',    a: 'c',   b: 'r',    type: 'directional' },
    { id: 'wC_cap',  a: 'c',   b: 'cap',  type: 'directional' },
    { id: 'wCap_S',  a: 'cap', b: 's',    type: 'directional' },
    { id: 'l1',      a: 'r',   b: 'sp',   type: 'locked' },
    { id: 'wSP_CORE',a: 'sp',  b: 'core', type: 'directional' },
  ],
  objective: { requiredNodeIds: [], coreNodeId: 'core' },
};

// ─────────────────────────────────────────────────────────────────────────────
// LEVEL 8 — Detour
// ─────────────────────────────────────────────────────────────────────────────
const L8: LevelDef = {
  id: 'l8',
  name: 'Detour',
  tier: 'medium',
  intro: 'Two starters look fine. They aren\'t. Find the one that skips the red.',
  hint: 'Trace each starter all the way down before you commit.',
  nodes: [
    { id: 's1', x: 270, y: 390, type: 'normal',    label: 'S1' },
    { id: 's2', x: 540, y: 390, type: 'normal',    label: 'S2' },
    { id: 's3', x: 810, y: 390, type: 'normal',    label: 'S3' },
    { id: 'p1', x: 270, y: 780, type: 'protected', label: 'P1' },
    { id: 'm',  x: 540, y: 780, type: 'normal',    label: 'M', conduit: true },
    { id: 'p2', x: 810, y: 780, type: 'protected', label: 'P2' },
    { id: 'r1', x: 360, y: 1170, type: 'required',  label: 'R1' },
    { id: 'r2', x: 720, y: 1170, type: 'required',  label: 'R2' },
  ],
  wires: [
    { id: 'w1', a: 's1', b: 'p1', type: 'directional' },
    { id: 'w2', a: 's1', b: 'r1', type: 'directional' },
    { id: 'w3', a: 's2', b: 'm',  type: 'directional' },
    { id: 'w4', a: 's3', b: 'p2', type: 'directional' },
    { id: 'w5', a: 's3', b: 'r2', type: 'directional' },
    { id: 'w6', a: 'm',  b: 'r1', type: 'directional' },
    { id: 'w7', a: 'm',  b: 'r2', type: 'directional' },
  ],
  objective: { requiredNodeIds: ['r1', 'r2'] },
};

// ─────────────────────────────────────────────────────────────────────────────
// LEVEL 9 — Forbidden Lines
// ─────────────────────────────────────────────────────────────────────────────
const L9: LevelDef = {
  id: 'l9',
  name: 'Forbidden Lines',
  tier: 'hard',
  intro: 'Dashed red wires trip an alarm the moment a pulse touches them.',
  hint: 'Follow each starter\'s outgoing wires. Red is a death sentence.',
  nodes: [
    { id: 's1', x: 270, y: 390, type: 'normal',   label: 'S1' },
    { id: 's2', x: 540, y: 390, type: 'normal',   label: 'S2' },
    { id: 's3', x: 810, y: 390, type: 'normal',   label: 'S3' },
    { id: 'h',  x: 540, y: 780, type: 'normal',   label: 'H', conduit: true },
    { id: 'r1', x: 270, y: 1170, type: 'required', label: 'R1' },
    { id: 'r2', x: 540, y: 1170, type: 'required', label: 'R2' },
    { id: 'r3', x: 810, y: 1170, type: 'required', label: 'R3' },
  ],
  wires: [
    { id: 'w1', a: 's1', b: 'h',  type: 'directional' },
    { id: 'w2', a: 's1', b: 'r2', type: 'forbidden' },
    { id: 'w3', a: 's2', b: 'h',  type: 'directional' },
    { id: 'w4', a: 's3', b: 'h',  type: 'directional' },
    { id: 'w5', a: 's3', b: 'r2', type: 'forbidden' },
    { id: 'w6', a: 'h',  b: 'r1', type: 'directional' },
    { id: 'w7', a: 'h',  b: 'r2', type: 'directional' },
    { id: 'w8', a: 'h',  b: 'r3', type: 'directional' },
  ],
  objective: { requiredNodeIds: ['r1', 'r2', 'r3'] },
};

// ─────────────────────────────────────────────────────────────────────────────
// LEVEL 10 — Shield Wall
// ─────────────────────────────────────────────────────────────────────────────
const L10: LevelDef = {
  id: 'l10',
  name: 'Shield Wall',
  tier: 'expert',
  intro: 'Shielded nodes absorb pulses harmlessly until the shield drops.',
  hint: 'A delay wire arrives later. Use it to land a pulse AFTER the shield is gone.',
  nodes: [
    { id: 's',   x: 540, y: 390, type: 'normal',   label: 'S' },
    { id: 'sh1', x: 270, y: 720, type: 'shield',   shields: ['r1'], label: 'SH1' },
    { id: 'a',   x: 540, y: 720, type: 'splitter', splitFactor: 2,  label: 'A' },
    { id: 'sh2', x: 810, y: 720, type: 'shield',   shields: ['r3'], label: 'SH2' },
    { id: 'r1',  x: 270, y: 1140, type: 'required', label: 'R1' },
    { id: 'r2',  x: 540, y: 1140, type: 'required', threshold: 2, decay: 4, label: 'R2' },
    { id: 'r3',  x: 810, y: 1140, type: 'required', label: 'R3' },
  ],
  wires: [
    { id: 'w1', a: 's',  b: 'sh1', type: 'directional' },
    { id: 'w2', a: 's',  b: 'a',   type: 'directional' },
    { id: 'w3', a: 's',  b: 'sh2', type: 'directional' },
    { id: 'w4', a: 'a',  b: 'r2',  type: 'directional' },
    { id: 'w5', a: 's',  b: 'r1',  type: 'delay' },
    { id: 'w6', a: 's',  b: 'r3',  type: 'delay' },
  ],
  objective: { requiredNodeIds: ['r1', 'r2', 'r3'] },
};

// ─────────────────────────────────────────────────────────────────────────────
// LEVEL 11 — Crossroads
// ─────────────────────────────────────────────────────────────────────────────
const L11: LevelDef = {
  id: 'l11',
  name: 'Crossroads',
  tier: 'expert',
  intro: 'Every starter looks plausible. Only one threads the whole maze.',
  hint: 'Splitters multiply pulses on every output. Relays open locked wires when they fall.',
  nodes: [
    { id: 's1',  x: 270, y: 360, type: 'normal',    label: 'S1' },
    { id: 's2',  x: 570, y: 360, type: 'normal',    label: 'S2' },
    { id: 's3',  x: 810, y: 360, type: 'normal',    label: 'S3' },
    { id: 'a',   x: 270, y: 690, type: 'splitter',  splitFactor: 2, label: 'A' },
    { id: 'b',   x: 570, y: 690, type: 'capacitor', label: 'B' },
    { id: 'rel', x: 810, y: 690, type: 'relay',     unlocks: ['lk1'], label: 'REL' },
    { id: 'sh',  x: 570, y: 1080, type: 'shield',    shields: ['r2'], label: 'SH' },
    { id: 'r1',  x: 270, y: 1440, type: 'required',  threshold: 2, decay: 5, label: 'R1' },
    { id: 'r2',  x: 570, y: 1440, type: 'required',  label: 'R2' },
    { id: 'r3',  x: 810, y: 1440, type: 'required',  label: 'R3' },
  ],
  wires: [
    { id: 'wS1_A', a: 's1', b: 'a',   type: 'directional' },
    { id: 'wS2_A', a: 's2', b: 'a',   type: 'directional' },
    { id: 'wS2_B', a: 's2', b: 'b',   type: 'directional' },
    { id: 'wS2_R', a: 's2', b: 'rel', type: 'directional' },
    { id: 'wS3_R', a: 's3', b: 'rel', type: 'directional' },
    { id: 'wA_R1', a: 'a',  b: 'r1',  type: 'directional' },
    { id: 'wB_SH', a: 'b',  b: 'sh',  type: 'delay' },
    { id: 'wB_R2', a: 'b',  b: 'r2',  type: 'delay', delay: 5 },
    { id: 'lk1',     a: 'rel', b: 'r2', type: 'locked' },
    { id: 'wREL_R3', a: 'rel', b: 'r3', type: 'directional' },
  ],
  objective: { requiredNodeIds: ['r1', 'r2', 'r3'] },
};

// ─────────────────────────────────────────────────────────────────────────────
// LEVEL 12 — The Citadel
// ─────────────────────────────────────────────────────────────────────────────
const L12: LevelDef = {
  id: 'l12',
  name: 'The Citadel',
  tier: 'nightmare',
  intro: 'The full system. One tap. Take it down.',
  hint: 'Every shield needs a dropper. Splitters supply convergence. Protected nodes are a trap.',
  nodes: [
    { id: 'a',   x: 150, y: 300, type: 'normal',    label: 'A' },
    { id: 'b',   x: 540, y: 300, type: 'normal',    label: 'B' },
    { id: 'c',   x: 930, y: 300, type: 'normal',    label: 'C' },

    { id: 'rel1',x: 300, y: 690, type: 'relay',     unlocks: ['lk_core'], label: 'REL' },
    { id: 'cap', x: 780, y: 690, type: 'capacitor', label: 'CAP' },

    { id: 'sh1', x: 150, y: 1050, type: 'shield',    shields: ['core'], label: 'SH-1' },
    { id: 'sp',  x: 540, y: 1050, type: 'splitter',  splitFactor: 3, label: 'SP' },
    { id: 'sh2', x: 930, y: 1050, type: 'shield',    shields: ['core'], label: 'SH-2' },

    { id: 'p',   x: 300, y: 1410, type: 'protected', label: 'P' },
    { id: 'core',x: 540, y: 1650,type: 'core',      threshold: 3, decay: 4, label: 'CORE' },
    { id: 'r',   x: 930, y: 1410, type: 'required',  label: 'R' },
  ],
  wires: [
    { id: 'wA_SH1', a: 'a',    b: 'sh1', type: 'directional' },
    { id: 'wA_REL', a: 'a',    b: 'rel1',type: 'directional' },
    { id: 'wA_CAP', a: 'a',    b: 'cap', type: 'directional' },
    { id: 'wA_SH2', a: 'a',    b: 'sh2', type: 'directional' },
    { id: 'wA_R',   a: 'a',    b: 'r',   type: 'directional' },
    { id: 'wB_P',   a: 'b',    b: 'p',   type: 'directional' },
    { id: 'wB_REL', a: 'b',    b: 'rel1',type: 'directional' },
    { id: 'wC_SH2', a: 'c',    b: 'sh2', type: 'directional' },
    { id: 'wC_R',   a: 'c',    b: 'r',   type: 'directional' },
    { id: 'wCAP_SP', a: 'cap',  b: 'sp',  type: 'delay' },
    { id: 'lk_core', a: 'rel1', b: 'core',type: 'locked' },
    { id: 'wSP_CORE',a: 'sp',   b: 'core',type: 'directional' },
  ],
  objective: { requiredNodeIds: ['r'], coreNodeId: 'core' },
};

// ═════════════════════════════════════════════════════════════════════════════
// CHAPTER 1 EXTRAS — PRIMER fill (CHAIN.04, FORK.05, MERGE.06)
// ═════════════════════════════════════════════════════════════════════════════

// L13 — CHAIN.04
// Linear 5-node chain. Tap the leftmost, the cascade walks all the way to the
// required at the end. Teaches "cascade propagates along wires".
const L13: LevelDef = {
  id: 'l13',
  name: 'Chain',
  tier: 'beginner',
  teaching: true,
  intro: 'Watch the cascade walk down the line.',
  hint: 'One tap. Five collapses. Just trust the chain.',
  nodes: [
    { id: 's', x: 150, y: 960, type: 'normal',   label: 'S' },
    { id: 'a', x: 345, y: 960, type: 'normal',   label: 'A' },
    { id: 'b', x: 540, y: 960, type: 'normal',   label: 'B' },
    { id: 'c', x: 735, y: 960, type: 'normal',   label: 'C' },
    { id: 'r', x: 930, y: 960, type: 'required', label: 'R' },
  ],
  wires: [
    { id: 'w1', a: 's', b: 'a' },
    { id: 'w2', a: 'a', b: 'b' },
    { id: 'w3', a: 'b', b: 'c' },
    { id: 'w4', a: 'c', b: 'r' },
  ],
  objective: { requiredNodeIds: ['r'] },
};

// L14 — FORK.05
// Y shape: tap the top, cascade splits into two requireds. Teaches "one
// collapse fires every open wire".
const L14: LevelDef = {
  id: 'l14',
  name: 'Fork',
  tier: 'beginner',
  teaching: true,
  intro: 'One pulse splits to feed both branches.',
  nodes: [
    { id: 's',  x: 540, y: 420,  type: 'normal',   label: 'S' },
    { id: 'y',  x: 540, y: 780,  type: 'normal',   label: 'Y' },
    { id: 'r1', x: 300, y: 1200, type: 'required', label: 'R1' },
    { id: 'r2', x: 780, y: 1200, type: 'required', label: 'R2' },
  ],
  wires: [
    { id: 'w1', a: 's', b: 'y',  type: 'directional' },
    { id: 'w2', a: 'y', b: 'r1', type: 'directional' },
    { id: 'w3', a: 'y', b: 'r2', type: 'directional' },
  ],
  objective: { requiredNodeIds: ['r1', 'r2'] },
};

// L15 — MERGE.06
// Diamond shape — two paths converging at a high-threshold required. Teaches
// that convergence is a thing without yet requiring a splitter.
const L15: LevelDef = {
  id: 'l15',
  name: 'Merge',
  tier: 'beginner',
  intro: 'Two paths down. Both have to land for the merge.',
  hint: 'Required nodes can need more than one pulse.',
  nodes: [
    { id: 's',  x: 540, y: 420,  type: 'normal',   label: 'S' },
    { id: 'a',  x: 300, y: 780,  type: 'normal',   label: 'A' },
    { id: 'b',  x: 780, y: 780,  type: 'normal',   label: 'B' },
    { id: 'r',  x: 540, y: 1200, type: 'required', threshold: 2, label: 'R' },
  ],
  wires: [
    { id: 'w1', a: 's', b: 'a', type: 'directional' },
    { id: 'w2', a: 's', b: 'b', type: 'directional' },
    { id: 'w3', a: 'a', b: 'r', type: 'directional' },
    { id: 'w4', a: 'b', b: 'r', type: 'directional' },
  ],
  objective: { requiredNodeIds: ['r'] },
};

// ═════════════════════════════════════════════════════════════════════════════
// CHAPTER 2 EXTRAS — QUARANTINE fill (DETOUR.08, MIRROR.09, BAIT.0B)
// ═════════════════════════════════════════════════════════════════════════════

// L16 — DETOUR.08
// Two completely separate columns. Left column starts at S1 and dead-ends in a
// protected node. Right column wins cleanly.
const L16: LevelDef = {
  id: 'l16',
  name: 'Detour',
  tier: 'easy',
  intro: 'Two paths. One ends in a red wall. Dim CONDUIT nodes only carry the pulse.',
  hint: 'Follow each starter to its conclusion before tapping.',
  nodes: [
    { id: 's1', x: 270, y: 420,  type: 'normal',    label: 'S1' },
    { id: 's2', x: 810, y: 420,  type: 'normal',    label: 'S2' },
    { id: 'a',  x: 270, y: 780,  type: 'normal',    label: 'A', conduit: true },
    { id: 'b',  x: 810, y: 780,  type: 'normal',    label: 'B', conduit: true },
    { id: 'p',  x: 270, y: 1140, type: 'protected', label: 'P' },
    { id: 'r',  x: 810, y: 1140, type: 'required',  label: 'R' },
  ],
  wires: [
    { id: 'w1', a: 's1', b: 'a', type: 'directional' },
    { id: 'w2', a: 'a',  b: 'p', type: 'directional' },
    { id: 'w3', a: 's2', b: 'b', type: 'directional' },
    { id: 'w4', a: 'b',  b: 'r', type: 'directional' },
  ],
  objective: { requiredNodeIds: ['r'] },
};

// L17 — MIRROR.09
// Symmetric hub layout — both starters feed the same center hub. One starter
// ALSO has a direct wire into the protected node. The other doesn't.
const L17: LevelDef = {
  id: 'l17',
  name: 'Mirror',
  tier: 'easy',
  intro: 'Symmetric, but only one side is clean.',
  hint: 'The losing starter has an extra wire pointing nowhere good.',
  nodes: [
    { id: 's1', x: 270, y: 420,  type: 'normal',    label: 'S1' },
    { id: 's2', x: 810, y: 420,  type: 'normal',    label: 'S2' },
    { id: 'p',  x: 270, y: 780,  type: 'protected', label: 'P' },
    { id: 'h',  x: 540, y: 780,  type: 'normal',    label: 'H', conduit: true },
    { id: 'r1', x: 300, y: 1200, type: 'required',  label: 'R1' },
    { id: 'r2', x: 780, y: 1200, type: 'required',  label: 'R2' },
  ],
  wires: [
    { id: 'w1', a: 's1', b: 'p', type: 'directional' },
    { id: 'w2', a: 's1', b: 'h', type: 'directional' },
    { id: 'w3', a: 's2', b: 'h', type: 'directional' },
    { id: 'w4', a: 'h',  b: 'r1', type: 'directional' },
    { id: 'w5', a: 'h',  b: 'r2', type: 'directional' },
  ],
  objective: { requiredNodeIds: ['r1', 'r2'] },
};

// L19 — BAIT.0B
// The "obvious" central starter routes through a protected mid-node. A subtler
// corner starter has a clean fan-out to both requireds.
const L19: LevelDef = {
  id: 'l19',
  name: 'Bait',
  tier: 'easy',
  intro: 'The flashy starter has a catch.',
  hint: 'Centered ≠ correct.',
  nodes: [
    { id: 'sc', x: 540, y: 420,  type: 'normal',    label: 'SC' },
    { id: 'sx', x: 210, y: 420,  type: 'normal',    label: 'SX' },
    { id: 'p',  x: 540, y: 780,  type: 'protected', label: 'P' },
    { id: 'r1', x: 360, y: 1140, type: 'required',  label: 'R1' },
    { id: 'r2', x: 720, y: 1140, type: 'required',  label: 'R2' },
  ],
  wires: [
    { id: 'w1', a: 'sc', b: 'p',  type: 'directional' },
    { id: 'w2', a: 'sc', b: 'r1', type: 'directional' },
    { id: 'w3', a: 'sx', b: 'r1', type: 'directional' },
    { id: 'w4', a: 'sx', b: 'r2', type: 'directional' },
  ],
  objective: { requiredNodeIds: ['r1', 'r2'] },
};

// ═════════════════════════════════════════════════════════════════════════════
// CHAPTER 3 EXTRAS — CONVERGENCE fill (TWIN.0D, BUFFER.0F)
// ═════════════════════════════════════════════════════════════════════════════

// L20 — TWIN.0D
// Two threshold-2 requireds. A single starter fans into both via a splitter.
const L20: LevelDef = {
  id: 'l20',
  name: 'Twin',
  tier: 'medium',
  intro: 'Two thresholds. One splitter. Land it.',
  hint: 'Splitters multiply outputs. Use them to feed both gates.',
  nodes: [
    { id: 's',  x: 540, y: 420,  type: 'normal',   label: 'S' },
    { id: 'sp', x: 540, y: 780,  type: 'splitter', splitFactor: 2, label: 'SP' },
    { id: 'r1', x: 300, y: 1200, type: 'required', threshold: 2, label: 'R1' },
    { id: 'r2', x: 780, y: 1200, type: 'required', threshold: 2, label: 'R2' },
  ],
  wires: [
    { id: 'w1', a: 's',  b: 'sp', type: 'directional' },
    { id: 'w2', a: 'sp', b: 'r1', type: 'directional' },
    { id: 'w3', a: 'sp', b: 'r2', type: 'directional' },
  ],
  objective: { requiredNodeIds: ['r1', 'r2'] },
};

// L21 — BUFFER.0F
// Capacitor buffers a pulse so it arrives at the required AFTER the direct
// pulse, giving threshold-2 convergence.
const L21: LevelDef = {
  id: 'l21',
  name: 'Buffer',
  tier: 'hard',
  intro: 'The capacitor holds a pulse just long enough.',
  hint: 'Required has decay, both pulses must land while the first is still warm.',
  nodes: [
    { id: 's',   x: 540, y: 420,  type: 'normal',    label: 'S' },
    { id: 'cap', x: 240, y: 780,  type: 'capacitor', label: 'CAP' },
    { id: 'd',   x: 840, y: 780,  type: 'normal',    label: 'D' },
    { id: 'r',   x: 540, y: 1320, type: 'required',  threshold: 2, decay: 4, label: 'R' },
  ],
  wires: [
    { id: 'w1', a: 's',   b: 'cap', type: 'directional' },
    { id: 'w2', a: 's',   b: 'd',   type: 'directional' },
    { id: 'w3', a: 'cap', b: 'r',   type: 'delay' },
    { id: 'w4', a: 'd',   b: 'r',   type: 'delay' },
  ],
  objective: { requiredNodeIds: ['r'] },
};

// ═════════════════════════════════════════════════════════════════════════════
// CHAPTER 4 EXTRAS — SHIELDS fill (PIERCE.11, FANOUT.13)
// ═════════════════════════════════════════════════════════════════════════════

// L23 — PIERCE.11
// First-shield tutorial-style level. Drop the shield via direct hit, then a
// delay-wire pulse arrives to crash the now-unguarded required.
const L23: LevelDef = {
  id: 'l23',
  name: 'Pierce',
  tier: 'expert',
  intro: 'Drop the guard with one wire. Land the kill with another.',
  hint: 'A delay wire arrives 3 ticks later. Use that to land AFTER the shield drops.',
  nodes: [
    { id: 's',  x: 540, y: 420,  type: 'normal',   label: 'S' },
    { id: 'sh', x: 300, y: 870,  type: 'shield',   shields: ['r'], label: 'SH' },
    { id: 'r',  x: 780, y: 1200, type: 'required', label: 'R' },
  ],
  wires: [
    { id: 'w1', a: 's', b: 'sh', type: 'directional' },
    { id: 'w2', a: 's', b: 'r',  type: 'delay' },
  ],
  objective: { requiredNodeIds: ['r'] },
};

// L25 — FANOUT.13
// Splitter feeds a high-threshold core. Two shields up; both have to drop
// before the splitter pulses arrive.
const L25: LevelDef = {
  id: 'l25',
  name: 'Fanout',
  tier: 'expert',
  intro: 'Two shields up. Three pulses converging. Get the timing right.',
  hint: 'Capacitor delays the splitter. By the time it fires, both shields are gone.',
  nodes: [
    { id: 's',    x: 540, y: 360, type: 'normal',    label: 'S' },
    { id: 'sh1',  x: 240, y: 720, type: 'shield',    shields: ['core'], label: 'SH1' },
    { id: 'cap',  x: 540, y: 720, type: 'capacitor', label: 'CAP' },
    { id: 'sh2',  x: 840, y: 720, type: 'shield',    shields: ['core'], label: 'SH2' },
    { id: 'sp',   x: 540, y: 1110, type: 'splitter', splitFactor: 3, label: 'SP' },
    { id: 'core', x: 540, y: 1500, type: 'core',     threshold: 3, decay: 4, label: 'CORE' },
  ],
  wires: [
    { id: 'w1', a: 's',   b: 'sh1', type: 'directional' },
    { id: 'w2', a: 's',   b: 'cap', type: 'directional' },
    { id: 'w3', a: 's',   b: 'sh2', type: 'directional' },
    { id: 'w4', a: 'cap', b: 'sp',  type: 'delay' },
    { id: 'w5', a: 'sp',  b: 'core',type: 'directional' },
  ],
  objective: { requiredNodeIds: [], coreNodeId: 'core' },
};

// ═════════════════════════════════════════════════════════════════════════════
// CHAPTER 5 EXTRAS — CITADEL fill (REACTOR.17, OMNIBUS.18)
// ═════════════════════════════════════════════════════════════════════════════

// L26 — REACTOR.17
// Decoy starters mixed with the winning starter. Heavy nightmare difficulty.
// Winner: A. Cascade drops both shields and feeds 3 pulses into the core via
// a capacitor-delayed splitter.
const L26: LevelDef = {
  id: 'l26',
  name: 'Reactor',
  tier: 'nightmare',
  intro: 'Two guards. One reactor. Three pulses to crash it.',
  hint: 'Shields drop on tick 1. Capacitor delays the splitter to tick 4. Time it.',
  nodes: [
    { id: 'a',    x: 180, y: 360, type: 'normal',    label: 'A' },
    { id: 'b',    x: 540, y: 360, type: 'normal',    label: 'B' },
    { id: 'c',    x: 900, y: 360, type: 'normal',    label: 'C' },
    { id: 'sh1',  x: 240, y: 780, type: 'shield',    shields: ['core'], label: 'SH1' },
    { id: 'cap',  x: 540, y: 780, type: 'capacitor', label: 'CAP' },
    { id: 'sh2',  x: 840, y: 780, type: 'shield',    shields: ['core'], label: 'SH2' },
    { id: 'p',    x: 300, y: 1170, type: 'protected', label: 'P' },
    { id: 'sp',   x: 540, y: 1170, type: 'splitter',  splitFactor: 3, label: 'SP' },
    { id: 'core', x: 540, y: 1560, type: 'core',     threshold: 3, decay: 4, label: 'CORE' },
  ],
  wires: [
    // A = winner: drops both shields AND feeds the splitter via capacitor
    { id: 'wA_SH1', a: 'a',   b: 'sh1', type: 'directional' },
    { id: 'wA_CAP', a: 'a',   b: 'cap', type: 'directional' },
    { id: 'wA_SH2', a: 'a',   b: 'sh2', type: 'directional' },
    // B = decoy: fires into the protected. Instant fail.
    { id: 'wB_P',   a: 'b',   b: 'p',   type: 'directional' },
    { id: 'wB_SH2', a: 'b',   b: 'sh2', type: 'directional' },
    // C = decoy: drops only one shield, no convergence.
    { id: 'wC_SH2', a: 'c',   b: 'sh2', type: 'directional' },
    // Convergence chain
    { id: 'wCAP_SP', a: 'cap', b: 'sp',  type: 'delay' },
    { id: 'wSP_CORE',a: 'sp',  b: 'core',type: 'directional' },
  ],
  objective: { requiredNodeIds: [], coreNodeId: 'core' },
};

// L27 — OMNIBUS.18
// Every mechanic in one level. Relay opens a locked wire that delivers the
// final pulse to the core after splitter pre-charges it.
const L27: LevelDef = {
  id: 'l27',
  name: 'Omnibus',
  tier: 'nightmare',
  intro: 'Every mechanic at once. Find the one tap that threads it.',
  hint: 'Relays open locked wires when they fall. Splitters supply the convergence.',
  nodes: [
    { id: 's1',   x: 240, y: 360, type: 'normal',    label: 'S1' },
    { id: 's2',   x: 540, y: 360, type: 'normal',    label: 'S2' },
    { id: 's3',   x: 840, y: 360, type: 'normal',    label: 'S3' },
    { id: 'rel',  x: 240, y: 720, type: 'relay',     unlocks: ['lk_sp'], label: 'REL' },
    { id: 'cap',  x: 540, y: 720, type: 'capacitor', label: 'CAP' },
    { id: 'sh',   x: 840, y: 720, type: 'shield',    shields: ['core'], label: 'SH' },
    { id: 'sp',   x: 540, y: 1110, type: 'splitter', splitFactor: 3, label: 'SP' },
    { id: 'p',    x: 240, y: 1110, type: 'protected', label: 'P' },
    { id: 'core', x: 540, y: 1500, type: 'core',     threshold: 3, decay: 4, label: 'CORE' },
    { id: 'r',    x: 870, y: 1380, type: 'required',  label: 'R' },
  ],
  wires: [
    // S2 = winner
    { id: 'wS2_REL', a: 's2', b: 'rel', type: 'directional' },
    { id: 'wS2_CAP', a: 's2', b: 'cap', type: 'directional' },
    { id: 'wS2_SH',  a: 's2', b: 'sh',  type: 'directional' },
    { id: 'wS2_R',   a: 's2', b: 'r',   type: 'directional' },
    // S1 = decoy (fires into protected via relay)
    { id: 'wS1_REL', a: 's1', b: 'rel', type: 'directional' },
    { id: 'wS1_P',   a: 's1', b: 'p',   type: 'directional' },
    // S3 = decoy (drops only the shield, no convergence)
    { id: 'wS3_SH',  a: 's3', b: 'sh',  type: 'directional' },
    // Relay unlocks a locked wire from itself to the splitter
    { id: 'lk_sp',   a: 'rel', b: 'sp',  type: 'locked' },
    // Capacitor delays a pulse to the splitter as well
    { id: 'wCAP_SP', a: 'cap', b: 'sp',  type: 'delay' },
    // Splitter spreads ×3 into the core
    { id: 'wSP_CORE',a: 'sp',  b: 'core',type: 'directional' },
  ],
  objective: { requiredNodeIds: ['r'], coreNodeId: 'core' },
};

/** Hand-authored, curated levels (have intros/hints, lead each tier). */
export const HAND_LEVELS: LevelDef[] = [
  L1, L2, L3, L4, L5, L6, L7, L8, L9, L10, L11, L12,
  L13, L14, L15, L16, L17, L19, L20, L21, L23, L25, L26, L27,
];

/**
 * Full shipping pool = curated levels + procedurally generated,
 * simulator-validated unique-solution levels (see scripts/gen-levels.ts).
 */
export const LEVELS: LevelDef[] = [...HAND_LEVELS, ...GENERATED_LEVELS, ...CODA_LEVELS];

export function getLevel(id: string): LevelDef | undefined {
  // Endless procedural levels ('p<n>') are generated on demand (and cached),
  // so the game ships infinite content without storing any of it.
  if (isProceduralId(id)) return generateLevel(proceduralIndex(id));
  if (isDailyId(id)) return generateDailyLevel(dailyKeyFromId(id));
  return LEVELS.find((l) => l.id === id);
}
